<script lang="ts">
  /**
   * ROUTE GUARD — Protects routes based on auth state & roles
   *
   * HOW IT WORKS:
   * 1. On every route change, svelte-spa-router fires a "conditionsFailed" event
   *    or we can use its `conditions` prop. But for maximum control, we use
   *    a simpler pattern: wrap the router and check BEFORE rendering.
   *
   * 2. This component reads the current hash path, looks up its RouteMeta,
   *    and decides:
   *    - Not logged in + requiresAuth? → Redirect to /login
   *    - Logged in + isPublic (login page)? → Redirect to /dashboard
   *    - Logged in but wrong role? → Show "Access Denied"
   *    - All checks pass? → Render the page (via <slot>)
   *
   * WHY a wrapper component instead of per-route checks?
   * Centralized logic. If you add 50 routes, you don't add 50 auth checks.
   */

  import { router, push } from 'svelte-spa-router';
  import { authState } from '../features/auth/auth.state.svelte';
  import { getRouteMeta } from './routes';
  import type { Snippet } from 'svelte';

  interface Props { children: Snippet; }
  let { children }: Props = $props();

  // $effect runs whenever its tracked reactive values change.
  // Here it tracks: $location (current route) and authState.isAuthenticated.
  // Every time the user navigates OR logs in/out, this re-evaluates.
  $effect(() => {
    const currentPath = router.location;
    const meta = getRouteMeta(currentPath);

    // GUARD 1: Protected route, but user is NOT logged in → redirect to login
    if (meta.requiresAuth && !authState.isAuthenticated) {
      push('/login');
      return;
    }

    // GUARD 2: Public-only route (login page), but user IS logged in → redirect to dashboard
    if (meta.isPublic && authState.isAuthenticated) {
      push('/dashboard');
      return;
    }

    // GUARD 3: Role check — user is logged in but doesn't have the right role
    if (meta.roles && meta.roles.length > 0 && authState.user) {
      if (!meta.roles.includes(authState.user.role)) {
        push('/dashboard'); // Redirect non-admins back to dashboard
        return;
      }
    }

    // GUARD 4: Update browser tab title
    document.title = `${meta.title} | Enterprise Dashboard`;
  });
</script>

<!-- If all guards pass, render whatever is inside this component (the router) -->
{@render children()}

<!-- 
  WHY {@render children()}?
  In Svelte 5, the old <slot /> syntax is replaced by snippet-based rendering.
  {@render children()} renders whatever the parent passes as content.
-->
